import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, useState} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useNavigationBlocking} from '../../../../srcCommon/app/navigationBlocking';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {ApplicationModel} from '../../../models/api/applicationModel';
import {
  LocalApplicationModel,
  serializeLocalApplication,
  UnsavedLocalApplicationModel,
} from '../../../models/api/localApplicationModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {
  SettingsApplicationComponentsEnum,
  SettingsApplicationsDetailsUrl,
  SettingsApplicationsListUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
} from '../../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {StyledEditorMain, StyledEditorWrapper} from '../../editors/commons/editorCommons';
import {DiscardChangesDialog} from '../../rules/ruleEditor/dialog/discardChangesDialog';
import {ResourceEditorFooter} from '../commons/resourceEditorFooter';

/*
 * Props.
 */

interface ApplicationEditorWrapperProps {
  panelId: SettingsPanelId;

  application: LocalApplicationModel | undefined;
  installedApplication: ApplicationModel | undefined;

  onSaveApplication: HandlerOf<UnsavedLocalApplicationModel>;
}

/*
 * Component.
 */

export const ApplicationEditorWrapper: FC<ApplicationEditorWrapperProps> = (props) => {
  const {panelId, application, installedApplication, onSaveApplication} = props;
  const originalName = application?.name ?? '';
  const [name, setName] = useState(originalName);

  const hasDiff = name !== originalName;

  const {navigateToLink} = useLinkNavigation();
  const navigateAfterCreateOrEdit = () => {
    navigateToLink(buildSettingsPath(buildLink(installedApplication)));
  };

  // Prevent the user from navigating away when there are unsaved changes.
  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, hasDiff);

  const onSaveButtonClick = () => {
    const updatedApplication = {
      ...(application ? serializeLocalApplication(application) : {}),
      name,
    };

    onSaveApplication(updatedApplication);
    unblockNavigation();
    navigateAfterCreateOrEdit();
  };

  return (
    <>
      <StyledEditorMain>
        <StyledEditorWrapper>
          <TextBox
            value={name}
            onChange={setName}
            size={VisualSizesEnum.LARGE}
            noMargin
            label={<Trans id="application-editor-rule-name-label">Name</Trans>}
            placeholder="Enter a name for this application"
            shouldAutoFocus
          />
        </StyledEditorWrapper>
        {didBlockNavigation && (
          <DiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />
        )}
      </StyledEditorMain>
      <ResourceEditorFooter
        isEditing={!isUndefined(application)}
        onCancel={() => navigateAfterCreateOrEdit()}
        onSave={onSaveButtonClick}
        createLabel={<Trans id="application-editor-footer-create">Create application</Trans>}
      />
    </>
  );
};

/*
 * Helpers.
 */

function buildLink(
  installedApplication?: ApplicationModel,
): SettingsApplicationsDetailsUrl | SettingsApplicationsListUrl {
  if (!installedApplication) {
    return {
      rootPage: SettingsRootPagesEnum.APPLICATIONS,
      componentPage: SettingsApplicationComponentsEnum.LIST,
      resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      searchQuery: undefined,
      applicationId: undefined,
    };
  }
  return {
    rootPage: SettingsRootPagesEnum.APPLICATIONS,
    componentPage: SettingsApplicationComponentsEnum.DETAILS,
    resourcePage: SettingsResourcePagesEnum.OVERVIEW,
    applicationId: installedApplication.id,
    searchQuery: undefined,
  };
}
